import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Settings,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface CompressorType {
  _id: string;
  name: string;
  description?: string;
  efficiency?: string;
  power?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

const CompressorTypesPage: React.FC = () => {
  const [compressorTypes, setCompressorTypes] = useState<CompressorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompressorType, setEditingCompressorType] = useState<CompressorType | null>(null);
  const [newCompressorType, setNewCompressorType] = useState({
    name: '',
    description: '',
    efficiency: '',
    power: '',
    status: 'active' as 'active' | 'inactive',
  });

  const { settings } = useSettings();

  useEffect(() => {
    fetchCompressorTypes();
  }, []);

  // Soft refresh for compressor types
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchCompressorTypes();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCompressorTypes = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/compressor-types?limit=1000'); // Request all compressor types
      setCompressorTypes(response.data.compressorTypes || []);
    } catch (error) {
      console.error('Error fetching compressor types:', error);
      toast.error('Failed to fetch compressor types');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompressorType = async () => {
    try {
      const response = await apiService.post('/compressor-types', newCompressorType);
      toast.success('Compressor type added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewCompressorType({
        name: '',
        description: '',
        efficiency: '',
        power: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCompressorTypes();
      }, 100);
    } catch (error) {
      toast.error('Failed to add compressor type');
    }
  };

  const handleEditCompressorType = async () => {
    if (!editingCompressorType) return;
    
    try {
      const response = await apiService.put(`/compressor-types/${editingCompressorType._id}`, editingCompressorType);
      toast.success('Compressor type updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingCompressorType(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCompressorTypes();
      }, 100);
    } catch (error) {
      toast.error('Failed to update compressor type');
    }
  };

  const handleDeleteCompressorType = async (compressorTypeId: string) => {
    if (window.confirm('Are you sure you want to delete this compressor type?')) {
      try {
        const response = await apiService.delete(`/compressor-types/${compressorTypeId}`);
        toast.success('Compressor type deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchCompressorTypes();
        }, 100);
      } catch (error) {
        console.error('Error deleting compressor type:', error);
        toast.error('Failed to delete compressor type');
      }
    }
  };

  const openEditModal = (compressorType: CompressorType) => {
    setEditingCompressorType(compressorType);
    setShowEditModal(true);
  };

  const filteredCompressorTypes = compressorTypes.filter(compressorType => {
    const matchesSearch = compressorType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         compressorType.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         compressorType.efficiency?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || compressorType.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const exportCompressorTypes = async () => {
    try {
      await apiService.download('/products/compressor-types/export', 'compressor-types.xlsx');
      toast.success('Compressor types exported successfully');
    } catch (error) {
      console.error('Error exporting compressor types:', error);
      toast.error('Failed to export compressor types');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6" style={{ borderRadius: 'var(--border-radius)', fontFamily: 'var(--font-family)', color: 'var(--color-text)' } as unknown as React.CSSProperties}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties}>Compressor Types</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties}>
            Manage your compressor types
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchCompressorTypes(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Compressor Type
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search compressor types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportCompressorTypes}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {/* Compressor Types Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Compressor Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Efficiency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Power</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredCompressorTypes.map((compressorType) => (
                <tr key={compressorType._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Settings className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {compressorType.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {compressorType.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {compressorType.efficiency || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {compressorType.power || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${compressorType.status === 'active' ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{compressorType.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(compressorType.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(compressorType)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompressorType(compressorType._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredCompressorTypes.length === 0 && (
          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No compressor types found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first compressor type.'}
            </p>
          </div>
        )}
      </div>
      {/* Add Compressor Type Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Compressor Type"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newCompressorType.name}
            onChange={e => setNewCompressorType({ ...newCompressorType, name: e.target.value })}
            placeholder="Enter compressor type name"
            required
          />
          <Input
            label="Description"
            value={newCompressorType.description}
            onChange={e => setNewCompressorType({ ...newCompressorType, description: e.target.value })}
            placeholder="Enter description"
          />
          <Input
            label="Efficiency"
            value={newCompressorType.efficiency}
            onChange={e => setNewCompressorType({ ...newCompressorType, efficiency: e.target.value })}
            placeholder="Enter efficiency"
          />
          <Input
            label="Power"
            value={newCompressorType.power}
            onChange={e => setNewCompressorType({ ...newCompressorType, power: e.target.value })}
            placeholder="Enter power"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newCompressorType.status}
              onChange={e => setNewCompressorType({ ...newCompressorType, status: e.target.value as 'active' | 'inactive' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddCompressorType}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Compressor Type
          </button>
        </div>
      </Modal>
      {/* Edit Compressor Type Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Compressor Type"
      >
        {editingCompressorType && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingCompressorType.name}
              onChange={e => setEditingCompressorType({ ...editingCompressorType, name: e.target.value })}
              placeholder="Enter compressor type name"
              required
            />
            <Input
              label="Description"
              value={editingCompressorType.description || ''}
              onChange={e => setEditingCompressorType({ ...editingCompressorType, description: e.target.value })}
              placeholder="Enter description"
            />
            <Input
              label="Efficiency"
              value={editingCompressorType.efficiency || ''}
              onChange={e => setEditingCompressorType({ ...editingCompressorType, efficiency: e.target.value })}
              placeholder="Enter efficiency"
            />
            <Input
              label="Power"
              value={editingCompressorType.power || ''}
              onChange={e => setEditingCompressorType({ ...editingCompressorType, power: e.target.value })}
              placeholder="Enter power"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingCompressorType.status}
                onChange={e => setEditingCompressorType({ ...editingCompressorType, status: e.target.value as 'active' | 'inactive' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditCompressorType}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Compressor Type
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CompressorTypesPage; 